import type { Store } from '@recipes/contracts';
import {
  Button,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
} from '@recipes/ui';
import { ArrowDownUp, Crosshair, LocateFixed, Store as StoreIcon } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getPlatform } from '@/lib/platform';
import { useShoppingAt } from './hooks';
import { proposeStore } from './lookup';
import { rememberPosition } from './position';
import { storeLabel } from './store-label';

const NONE = '__none__';

/**
 * "Shopping at …" (docs/finished/ingredient-catalog-spec.md 5.5): a view setting of this device that
 * filters the hints. With location access the nearest known branch within 150 m is proposed –
 * the comparison runs on the device against the local stores, no position leaves it.
 */
function useShoppingAtProposal() {
  const at = useShoppingAt();
  const [proposal, setProposal] = useState<Store | null>(null);
  const [locating, setLocating] = useState(false);
  // Why the locate button gave nothing (user, 2026-09-25: "what does it mean when the picker
  // does not work?") – only after a tap, never for the silent attempt at start.
  const [notice, setNotice] = useState<LocateNotice>(null);
  const hasBranches = at.stores.some((s) => s.lat !== null);

  const locate = async (silent: boolean) => {
    setLocating(true);
    setNotice(null);
    try {
      const platform = await getPlatform();
      if (silent && (await platform.locationPermission()) !== 'granted') return;
      const position = await platform.getCurrentPosition({ timeoutMs: 8000, maximumAgeMs: 60_000 });
      rememberPosition(position);
      const nearest = await proposeStore(position);
      const found = nearest && nearest.id !== at.storeId ? nearest : null;
      setProposal(found);
      // Standing in the store chosen already: the line under the row says so, nothing to add.
      if (!silent && !nearest) setNotice('noneNearby');
    } catch {
      setProposal(null);
      if (!silent) setNotice('unavailable');
    } finally {
      setLocating(false);
    }
  };

  // Propose on our own only when the permission was given before – never prompt unasked.
  useEffect(() => {
    if (hasBranches) void locate(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasBranches]);

  const accept = () => {
    if (!proposal) return;
    at.setStoreId(proposal.id);
    setProposal(null);
  };
  return {
    ...at,
    proposal,
    dismiss: () => setProposal(null),
    accept,
    locating,
    locate,
    notice,
    hasBranches,
  };
}

type LocateNotice = 'unavailable' | 'noneNearby' | null;

function LocateNoticeLine({ notice }: { notice: LocateNotice }) {
  const { t } = useTranslation();
  if (!notice) return null;
  return (
    <p
      className="mt-1 text-xs text-muted-foreground"
      role="status"
      data-testid="shopping-at-notice"
    >
      {notice === 'unavailable'
        ? t('catalog.shoppingAt.unavailable')
        : t('catalog.shoppingAt.noneNearby')}
    </p>
  );
}

function StoreSelect({
  id,
  storeId,
  stores,
  onChange,
}: {
  id: string;
  storeId: string | null;
  stores: Store[];
  onChange: (id: string | null) => void;
}) {
  const { t } = useTranslation();
  return (
    <Select value={storeId ?? NONE} onValueChange={(v) => onChange(v === NONE ? null : v)}>
      <SelectTrigger id={id} className="mt-1" data-testid="shopping-at-select">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={NONE}>{t('catalog.shoppingAt.none')}</SelectItem>
        {stores.map((s) => (
          <SelectItem key={s.id} value={s.id}>
            {storeLabel(s)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function Proposal({
  proposal,
  onAccept,
  onDismiss,
}: {
  proposal: Store;
  onAccept: () => void;
  onDismiss: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div
      className="mt-2 flex flex-wrap items-center gap-2 rounded-md bg-muted px-3 py-2 text-sm"
      role="status"
      data-testid="shopping-at-proposal"
    >
      <span className="flex-1">
        {t('catalog.shoppingAt.proposal', { store: storeLabel(proposal) })}
      </span>
      <Button size="sm" onClick={onAccept} data-testid="shopping-at-accept">
        {t('catalog.shoppingAt.accept')}
      </Button>
      <Button size="sm" variant="ghost" onClick={onDismiss}>
        {t('common.no')}
      </Button>
    </div>
  );
}

/** The full control: label, select, locate button and the proposal (scan page, receipt capture). */
export function ShoppingAtControl({ className }: { className?: string }) {
  const { t } = useTranslation();
  const id = useId();
  const at = useShoppingAtProposal();
  if (at.stores.length === 0) return null;

  return (
    <div className={className} data-testid="shopping-at">
      <div className="flex items-end gap-2">
        <div className="min-w-0 flex-1">
          <Label htmlFor={id} className="flex items-center gap-1 text-xs">
            <StoreIcon className="size-3.5" aria-hidden /> {t('catalog.shoppingAt.label')}
          </Label>
          <StoreSelect id={id} storeId={at.storeId} stores={at.stores} onChange={at.setStoreId} />
        </div>
        {at.hasBranches && (
          <Button
            type="button"
            variant="outline"
            size="icon"
            loading={at.locating}
            aria-label={t('catalog.shoppingAt.locate')}
            onClick={() => void at.locate(false)}
          >
            <LocateFixed aria-hidden />
          </Button>
        )}
      </div>
      <LocateNoticeLine notice={at.notice} />
      {at.proposal && (
        <Proposal proposal={at.proposal} onAccept={at.accept} onDismiss={at.dismiss} />
      )}
      {at.store && (
        <span className="sr-only">{t('catalog.shoppingAt.current', { store: at.store.name })}</span>
      )}
    </div>
  );
}

/**
 * The compact form for the shopping list (user, 2026-09-25): a crosshair in the sort row –
 * grey without a store, in the primary colour with one, in the warning colour while a store
 * found by GPS waits for a yes. A tap opens the choice or the confirmation; the store itself
 * stands as a small line under the row (`ShoppingAtLine`).
 */
export function ShoppingAtButton({
  hideElsewhere,
  onHideElsewhere,
  hiddenCount,
  onDepartmentOrder,
}: {
  hideElsewhere: boolean;
  onHideElsewhere: (hide: boolean) => void;
  hiddenCount: number;
  /** Present when the chosen store's department order may be edited. */
  onDepartmentOrder?: () => void;
}) {
  const { t } = useTranslation();
  const id = useId();
  const at = useShoppingAtProposal();
  const [open, setOpen] = useState(false);
  if (at.stores.length === 0) return null;
  const state = at.proposal ? 'proposal' : at.store ? 'chosen' : 'none';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className={cn(
            'h-9 w-9 shrink-0',
            state === 'chosen' && 'border-primary text-primary',
            state === 'proposal' && 'border-warning text-warning',
          )}
          aria-label={
            at.store
              ? t('catalog.shoppingAt.at', { store: storeLabel(at.store) })
              : t('catalog.shoppingAt.button')
          }
          title={t('catalog.shoppingAt.button')}
          data-testid="shopping-at-button"
          data-state-store={state}
        >
          <Crosshair aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 space-y-3" data-testid="shopping-at">
        {at.proposal && (
          <Proposal
            proposal={at.proposal}
            onAccept={() => {
              at.accept();
              setOpen(false);
            }}
            onDismiss={at.dismiss}
          />
        )}
        <div className="flex items-end gap-2">
          <div className="min-w-0 flex-1">
            <Label htmlFor={id} className="flex items-center gap-1 text-xs">
              <StoreIcon className="size-3.5" aria-hidden /> {t('catalog.shoppingAt.label')}
            </Label>
            <StoreSelect id={id} storeId={at.storeId} stores={at.stores} onChange={at.setStoreId} />
          </div>
          {at.hasBranches && (
            <Button
              type="button"
              variant="outline"
              size="icon"
              loading={at.locating}
              aria-label={t('catalog.shoppingAt.locate')}
              onClick={() => void at.locate(false)}
              data-testid="shopping-at-locate"
            >
              <LocateFixed aria-hidden />
            </Button>
          )}
        </div>
        <LocateNoticeLine notice={at.notice} />
        {at.store && (
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={hideElsewhere}
              onChange={(e) => onHideElsewhere(e.target.checked)}
              data-testid="shopping-hide-elsewhere"
            />
            {t('catalog.shoppingAt.hideElsewhere')}
            {hiddenCount > 0 && (
              <span className="text-xs text-muted-foreground">
                {t('catalog.shoppingAt.hidden', { count: hiddenCount })}
              </span>
            )}
          </label>
        )}
        {at.store && onDepartmentOrder && (
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2"
            onClick={() => {
              setOpen(false);
              onDepartmentOrder();
            }}
            data-testid="shopping-department-order"
          >
            <ArrowDownUp aria-hidden /> {t('catalog.departments.action')}
          </Button>
        )}
      </PopoverContent>
    </Popover>
  );
}

/** The small line under the sort row: where I am shopping. */
export function ShoppingAtLine({ className }: { className?: string }) {
  const { t } = useTranslation();
  const { store, stores } = useShoppingAt();
  if (stores.length === 0 || !store) return null;
  return (
    <p
      className={cn('text-xs text-muted-foreground', className)}
      role="status"
      data-testid="shopping-at-current"
    >
      {t('catalog.shoppingAt.at', { store: storeLabel(store) })}
    </p>
  );
}
